import {AfterViewInit, Component, OnDestroy, OnInit, TemplateRef, ViewChild, ViewEncapsulation} from '@angular/core';
import {Router} from '@angular/router';
import {Subject, takeUntil} from 'rxjs';
import {FuseMediaWatcherService} from '@fuse/services/media-watcher';
import {FuseNavigationService, FuseVerticalNavigationComponent, FuseNavigationItem} from '@fuse/components/navigation';
import {Navigation} from 'app/core/navigation/navigation.types';
import {NavigationService} from 'app/core/navigation/navigation.service';
import {LocationService} from "../../../../shared/services/location.service";
import {AllLocationResponse, AllLocationResponsePagination} from "../../../../shared/models/location.types";
import {HomeService} from "../../../../modules/admin/dashboard/services/home.service";
import {MyOrgSubscription} from "../../../../modules/admin/dashboard/models/my-org-subscription";
import {DateTime, Interval} from "luxon";
import {MatDialog} from "@angular/material/dialog";
import {ShortcutsService} from "../../../common/shortcuts/shortcuts.service";
import {Device} from "@capacitor/device";
import {AuthService} from "../../../../core/auth/auth.service";
import {User} from "../../../../shared/models/user.types";
import {AuthUtils} from "../../../../core/auth/auth.utils";
import {MatSnackBar} from "@angular/material/snack-bar";
import {TranslocoService} from "@ngneat/transloco";
import {WhiteLabelProperty} from "../../../../modules/admin/agency-mode/models/white-label-property";
import {WhiteLabelPropertyService} from "../../../../modules/admin/agency-mode/services/white-label-property.service";
import {FaviconService} from "../../../../shared/services/favicon.service";
import {PageEvent} from "@angular/material/paginator";
import {
    SelectLocationDialogComponent
} from "../../../../shared/components/select-location-dialog/select-location-dialog.component";


@Component({
    selector: 'compact-layout',
    templateUrl: './compact.component.html',
    encapsulation: ViewEncapsulation.None
})
export class CompactLayoutComponent implements OnInit, OnDestroy, AfterViewInit {
    @ViewChild('subscriptionExpired') subscriptionExpired: TemplateRef<any>;
    @ViewChild('selectLocationDialog') selectLocationDialog: TemplateRef<any>;
    isScreenSmall: boolean;
    rawNavigation: Navigation;
    navigation: Navigation;
    private unsubscribeAll: Subject<any> = new Subject<any>();
    showSideBar = false;
    currentLocation: AllLocationResponse;
    locationCredit = 0;
    orgSubscription: MyOrgSubscription;
    setupProgressStatus = 3;
    isMobileDevice = false;
    isAppleDevice = false;
    user: User;
    isEmailVerified = false;
    private channel: BroadcastChannel;
    message = '';
    emailVerifyRequestDate = '';
    whiteLabelProperty: WhiteLabelProperty;


    constructor(private router: Router,
                private navigationService: NavigationService,
                private fuseMediaWatcherService: FuseMediaWatcherService,
                private fuseNavigationService: FuseNavigationService,
                private locationService: LocationService,
                private homeService: HomeService,
                private matDialog: MatDialog,
                private shortcutService: ShortcutsService,
                private authService: AuthService,
                private snackBar: MatSnackBar,
                private translocoService: TranslocoService,
                private whiteLabelPropertyService: WhiteLabelPropertyService,
                private faviconService: FaviconService) {
        this.channel = new BroadcastChannel('shared_channel');
    }

    get currentYear(): number {
        return new Date().getFullYear();
    }

    ngOnInit(): void {
        this.emailVerifyRequestDate = localStorage.getItem("emailVerifyRequestDate") || '';
        this.user = this.authService.businessUser;
        this.homeService.currentNativeDeviceInfo.subscribe(isNative => {
            this.isMobileDevice = isNative;
            if (!isNative && this.homeService.isNativeDevice) {
                this.homeService.nativeDeviceInfo.next(this.homeService.isNativeDevice);
            }
        });
        this.homeService.currentOrgSubscriptionInfo.subscribe(orgSubscription => {
            if (orgSubscription) {
                this.orgSubscription = orgSubscription;
            } else {
                if (this.homeService.orgSubscription) {
                    this.homeService.orgSubscriptionInfo.next(this.homeService.orgSubscription);
                    this.orgSubscription = this.homeService.orgSubscription;
                }
            }
            this.locationCredit = this.orgSubscription && this.orgSubscription.active ? this.orgSubscription.locationCredit : 0;
            this.filterNavigation();
        });
        // Subscribe to navigation data
        this.navigationService.navigation$
            .pipe(takeUntil(this.unsubscribeAll))
            .subscribe((navigation: Navigation) => {
                this.rawNavigation = navigation;
                this.filterNavigation();
            });
        this.fuseMediaWatcherService.onMediaChange$
            .pipe(takeUntil(this.unsubscribeAll))
            .subscribe(({matchingAliases}) => {
                this.isScreenSmall = !matchingAliases.includes('md');
            });
        this.locationService.currentLocation.subscribe(res => {
            this.showSideBar = false;
            this.currentLocation = null;
            if (res && res.locationId) {
                this.currentLocation = res;
            } else {
                if (this.locationService.currentLocationDetail && this.locationService.currentLocationDetail.locationId) {
                    this.locationService.locationDetail.next(this.locationService.currentLocationDetail);
                    this.currentLocation = this.locationService.currentLocationDetail;
                }
            }
            this.showSideBar = !!(this.currentLocation && this.currentLocation.locationId);
            this.filterNavigation();
        });
        const logDeviceInfo = async () => {
            const info = await Device.getInfo();
            this.isAppleDevice = info.platform == 'ios';
        };
        logDeviceInfo().then(info => {
            if (info != undefined) {
                this.isAppleDevice = info['platform'] == 'ios';
            }
        });
        this.isEmailVerified = AuthUtils.IsEmailVerified(this.authService.accessToken);
        this.channel.onmessage = (event) => {
            if (event.data.data == 'Token is refreshed.') {
                this.isEmailVerified = AuthUtils.IsEmailVerified(this.authService.idToken);
                this.authService.accessToken = this.authService.idToken;
            }
        };
        if (this.orgSubscription && this.orgSubscription.whitelabelEnabled) {
            this.whiteLabelPropertyService.currentWhiteLabelPropertyInfo.subscribe(property => {
                this.whiteLabelProperty = property;
                if (property) {
                    if (property.whiteLabelEnabled) {
                        this.faviconService.setFavicon(property.faviconUrl, property.titleText);
                    } else {
                        this.faviconService.setFavicon('assets/images/logo/logo-fr.svg');
                    }
                } else {
                    const data = this.whiteLabelPropertyService.whiteLabelProperty;
                    if (data) {
                        this.whiteLabelPropertyService.whiteLabelProperty = this.whiteLabelPropertyService.whiteLabelProperty;
                    } else {
                        this.faviconService.setFavicon('assets/images/logo/logo-fr.svg');
                    }
                }
            })
        } else {
            this.faviconService.setFavicon('assets/images/logo/logo-fr.svg');
        }
    }

    ngOnDestroy(): void {
        // Unsubscribe from all subscriptions
        this.unsubscribeAll.next(null);
        this.unsubscribeAll.complete();
    }

    filterNavigation(): void {
        if (!this.rawNavigation) {
            return;
        }
        const navigation: Navigation = {...this.rawNavigation};
        navigation.compact = this.rawNavigation.compact
            .filter((item) => this.isNavItemVisible(item))
            .map(item => this.filterNavItemChildren(item));
        this.navigation = navigation;
    }

    private isNavItemVisible(item: FuseNavigationItem, checkHideOnSubscription = false): boolean {
        if (checkHideOnSubscription && item.hideOnSubscription && (
            (this.orgSubscription && this.orgSubscription.hideSubscription) ||
            (this.currentLocation && this.currentLocation.gpGarageId)
        )) {
            return false;
        }
        return (!item.roleType || item.roleType.includes(this.user.roleType))
            && (!item.orgKey || (this.orgSubscription && this.orgSubscription[item.orgKey]))
            && (!item.setupType || (this.user && item.setupType && item.setupType.includes(this.user.type)));
    }

    private filterNavItemChildren(item: FuseNavigationItem): FuseNavigationItem {
        if (!item.children) {
            return {...item};
        }
        const children = item.children
            .filter(child => this.isNavItemVisible(child, true))
            .map(child => this.filterNavItemChildren(child))
            .filter(child =>
                (child.type !== 'group' && child.type !== 'collapsable')
                || (child.children && child.children.length > 0)
            );

        return {...item, children};
    }

    toggleNavigation(name: string): void {
        const navigation = this.fuseNavigationService.getComponent<FuseVerticalNavigationComponent>(name);
        if (navigation) {
            navigation.toggle();
        }
    }

    ngAfterViewInit() {
        const redirectURL = this.router.url;
        if (!redirectURL.includes('agency-mode') && redirectURL != '/dashboard/select-location' && redirectURL != '/dashboard/quick-setup') {
            const orgSubscription = JSON.parse(localStorage.getItem('org_subscription'));
            if (!orgSubscription || !orgSubscription['active']) {
                this.matDialog.open(this.subscriptionExpired, {
                    hasBackdrop: true,
                    disableClose: true,
                    backdropClass: 'opacity-5',
                });
            }
        }
        setTimeout(() => {
            this.shortcutService.currentSetupProgress.subscribe(progress => {
                this.setupProgressStatus = progress;
            });
            this.setupProgressStatus = this.shortcutService.currentSetupProgressDetail;
        }, 100);
    }


    closeDialog() {
        this.matDialog.closeAll();
    }

    goToPlan() {
        this.matDialog.closeAll();
        this.router.navigateByUrl('/agency-mode/pricing');
    }

    verifyEmailAddress() {
        if (this.emailVerifyRequestDate && DateTime.now().diff(DateTime.fromISO(this.emailVerifyRequestDate), 'minutes').minutes < 1) {
            this.snackBar.open(this.translocoService.translate('Please try after sometime.'), '', {duration: 3000});
            return;
        }
        this.emailVerifyRequestDate = new Date().toISOString();
        localStorage.setItem('emailVerifyRequestDate', this.emailVerifyRequestDate);
        this.authService.sendVerificationMail().subscribe({
            next: (res: string) => {
                this.snackBar.open(this.translocoService.translate('Please Check Spam Folder'), '', {
                    duration: 3000,
                });
            },
            error: (error) => {
                this.snackBar.open(this.translocoService.translate('Please Contact Administrator'), '', {
                    duration: 3000,
                });
            }
        });
    }

    openChangeLocationDialog() {
        this.matDialog.open(SelectLocationDialogComponent).afterClosed().subscribe((currentLocation: AllLocationResponse) => {
            if (!this.currentLocation || (this.currentLocation && this.currentLocation.locationId != currentLocation.locationId)) {
                this.locationService.currentLocationDetail = currentLocation;
            }
            this.router.navigate(['dashboard']).then(() => {
                location.reload();
            });
        });
    }
}
